import Back from 'components/Back';
import {
  Button, Card, Container, ErrorMessage,
} from 'nhsuk-react-components';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useUpdateTeamRoutingPlans from 'queries/mutate/updateTeamRoutingPlans';
import { NotifyRoutingPlan } from 'schemas/NotifyRoutingPlan.schema';
import { RoutingPlan } from 'schemas/RoutingPlan.schema';
import { mapNotifyRoutingPlansToSpi, parseRoutingPlansFromClipboard } from 'utils/routingPlansUtils';
import featureFlags from 'services/featureFlags';
import useFetchTeamRoutingPlans from 'queries/fetch/fetchTeamRoutingPlans.ts';
import ErrorPage from 'pages/ErrorPage.tsx';
import TeamDetailsSection from '../TeamDetailsSection';
import AdminRoutingPlansSection from './AdminRoutingPlansSection';
import './TeamRoutingPlans.css';
import AdminRoutingLegacyPlansSection from './AdminRoutingLegacyPlansSection';

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function AddRoutingPlansPage() {
  const { teamId } = useParams() as { teamId: string };
  const { data: existingPlans, error: fetchError } = useFetchTeamRoutingPlans({ teamId });
  const navigate = useNavigate();
  const { mutate: updateTeamRoutingPlans, error: updateError, reset: resetUpdateTeamRoutingPlans } = useUpdateTeamRoutingPlans();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [notifyRoutingPlans, setNotifyRoutingPlans] = useState<NotifyRoutingPlan[]>([]);

  const handlePasteFromClipboard = async (): Promise<void> => {
    (document.activeElement as HTMLElement)?.blur();
    resetUpdateTeamRoutingPlans();
    setErrorMessage('');

    try {
      const result = await parseRoutingPlansFromClipboard();

      if (!result.success || result.data.length === 0) {
        setErrorMessage('No valid message plans found on clipboard');
        return;
      }

      const parsedPlans = result.data;

      if (parsedPlans.some((plan) => !GUID_REGEX.test(plan.routing_plan_id.trim()))) {
        setErrorMessage('No valid message plans found on clipboard');
        return;
      }

      if (parsedPlans.some((plan) => plan.status === 'LEGACY')) {
        setErrorMessage('No valid message plans found on clipboard');
        return;
      }

      const legacyPlans = notifyRoutingPlans.filter((plan) => plan.status === 'LEGACY');
      setNotifyRoutingPlans([...legacyPlans, ...parsedPlans]);
    } catch {
      setErrorMessage('Failed to read from clipboard. Please ensure you have granted clipboard permissions and try again.');
    }
  };

  const handleRemovePastedPlans = (): void => {
    resetUpdateTeamRoutingPlans();
    setNotifyRoutingPlans((prevPlans) => prevPlans.filter((plan) => plan.status === 'LEGACY'));
    setErrorMessage('');
  };

  const handleDeletePlan = (plan: NotifyRoutingPlan): void => {
    setNotifyRoutingPlans((prevPlans) => prevPlans.filter((p) => p !== plan));
  };

  const handleAddLegacyPlan = (planName: string): void => {
    (document.activeElement as HTMLElement)?.blur();
    setErrorMessage('');

    const duplicatesWithExisting = notifyRoutingPlans.find((plan) => planName.includes(plan.routing_plan_name));

    if (duplicatesWithExisting) {
      setErrorMessage('This message plan name already exists. Message plan names must be unique and cannot have the same name.');
      return;
    }

    setNotifyRoutingPlans((prevPlans) => [
      ...prevPlans,
      {
        routing_plan_id: planName,
        routing_plan_name: planName,
        status: 'LEGACY',
      } as NotifyRoutingPlan,
    ]);
  };

  const handleOnSaveAndContinue = (): void => {
    updateTeamRoutingPlans({
      teamId,
      routingMappings: {
        routing_mappings: mapNotifyRoutingPlansToSpi(notifyRoutingPlans, existingPlans as RoutingPlan[]),
      },
    }, {
      onSuccess: () => {
        navigate(`/admin/${teamId}/team-message-plans/confirmation`);
      },
    });
  };

  if (fetchError) {
    return <ErrorPage />;
  }

  return (
    <Container>
      <div className="nhsuk-u-margin-top-6 nhsuk-u-margin-bottom-6">
        <Back backTo={`/admin/${teamId}/team-message-plans`} />
      </div>
      <Card>
        <>
          <h1
            role="main"
            className="nhsuk-heading-l nhsuk-u-margin-top-0 nhsuk-u-margin-bottom-3"
          >
            Add message plans
          </h1>
          <TeamDetailsSection teamId={teamId} />
        </>
      </Card>
      <Card>
        <AdminRoutingPlansSection
          errorMessage={errorMessage}
          notifyRoutingPlans={notifyRoutingPlans.filter((item) => item.status === 'DRAFT' || item.status === 'COMPLETED')}
          onPasteFromClipboard={handlePasteFromClipboard}
          onDeletePlan={handleDeletePlan}
        />
      </Card>

      {featureFlags.legacyMessagePlansEnabled && (
        <AdminRoutingLegacyPlansSection
          notifyRoutingPlans={notifyRoutingPlans.filter((item) => item.status === 'LEGACY')}
          onDeletePlan={handleDeletePlan}
          onAddLegacyPlan={handleAddLegacyPlan}
        />
      )}
      {updateError && (
        <ErrorMessage>{updateError.message}</ErrorMessage>
      )}

      {notifyRoutingPlans.length > 0 && (
        <div className="nhsuk-u-margin-top-4">
          <Button
            className="nhsuk-u-margin-right-3 routing-plans-table__remove-pasted-plans-button"
            onClick={handleRemovePastedPlans}
          >
            Remove pasted plans
          </Button>
          <Button onClick={handleOnSaveAndContinue}>Save and continue</Button>
        </div>
      )}
    </Container>
  );
}

export default AddRoutingPlansPage;
